'use client';

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalendarDays, Home, LogOut, Settings, User } from 'lucide-react';
import type { UserWithCommunities } from '@/types';
import { useTenant } from '@/providers/tenant-context';
import { Link } from 'react-router-dom';

interface AccountHeaderProps {
  user?: UserWithCommunities | null;
}

export function AccountHeader({ user }: AccountHeaderProps) {
  const navigate = useNavigate();
  const { tenant, isWhitelabel } = useTenant();

  const handleSignOut = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <header className="h-16 flex items-center justify-between px-4 lg:px-8 border-b border-border bg-background/95 backdrop-blur">
      <Link className="flex items-center gap-2.5 group" to="/">
        {isWhitelabel && tenant?.logoWithName ? (
          <img
            src={tenant.logoWithName}
            alt={tenant.name}
            className="h-8 w-auto object-contain group-hover:scale-105 transition-all"
          />
        ) : (
          <>
            <img
              src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
              alt={isWhitelabel ? tenant?.name : 'Milora'}
              className="w-8 h-8 object-contain group-hover:scale-105 transition-all"
            />
            <span className="font-bold text-xl tracking-tighter text-foreground">
              {isWhitelabel ? tenant?.name : 'Milora'}
            </span>
          </>
        )}
      </Link>
      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 rounded-xl font-medium">
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {user.firstName?.[0]}
                  {user.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{user.firstName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/events')}>
              <CalendarDays className="mr-2 h-4 w-4" /> Networking events
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/dashboard')}>
              <Home className="mr-2 h-4 w-4" /> Go home
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/account-settings')}>
              <Settings className="mr-2 h-4 w-4" /> Account Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-red-500">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-2">
          <Button variant="ghost" asChild className="rounded-xl font-medium">
            <Link to="/login">Log in</Link>
          </Button>
          <Button className="rounded-xl font-semibold shadow-sm" asChild>
            <Link to="/register">Sign up</Link>
          </Button>
        </div>
      )}
    </header>
  );
}
